import React, { useRef, useState, useEffect } from 'react';
import RichTextA11yWrapper from 'helpers/RichTextA11yWrapper/RichTextA11yWrapper';
import SVG from 'helpers/SVG/SVG';
import { BoKNavigation } from 'lib/templates/Feature.BOKF.model';

type LoginLinkCollection = BoKNavigation.NavigationLink & {
  fields: {
    children: BoKNavigation.NavigationLink[];
  };
};

type AdditionalLinksCollection = BoKNavigation.LoginModalLinks & {
  fields: {
    children: LoginLinkCollection[];
  };
};

interface AdditionalLinksModalProps {
  showModal: boolean;
  setShowModal: (show: boolean) => void;
  additionalLinks: AdditionalLinksCollection | undefined;
  onGoBack?: () => void; // Optional callback to return directly to login without flicker
  onCloseAll?: () => void; // Optional callback to close both AdditionalLinks and parent login modal
}

const AdditionalLinksModal: React.FC<AdditionalLinksModalProps> = ({
  showModal,
  setShowModal,
  additionalLinks,
  onGoBack,
  onCloseAll,
}) => {
  const [filter, setFilter] = useState('');
  const [hasResults, setHasResults] = useState(false);
  const [enableClearSearch, setClearSearch] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const linkDialog = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);

  function handleEscape(e: { key: string }) {
    if (e.key === 'Escape') {
      setShowModal(false);
      setFilter('');
    }
  }

  function filterLinks(e: string) {
    if (e.length === 1) {
      setClearSearch(true);
    } else if (e.length >= 2) {
      setClearSearch(true);
      setFilter(e);
    } else {
      setClearSearch(false);
      setFilter('');
      setHasResults(true);
    }
  }

  function clearSearch() {
    setFilter('');
    setClearSearch(false);
    if (searchInputRef.current != null) {
      searchInputRef.current.value = '';
      searchInputRef.current.focus();
    }
  }

  useEffect(() => {
    if (showModal) {
      document.addEventListener('keydown', handleEscape);
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.documentElement.style.overflow = '';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
    // handleEscape is stable and does not need to be in deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal]);

  useEffect(() => {
    const activeCategories = document.querySelectorAll('[data-link-category]');
    const hasVisibleCategories = Array.from(activeCategories).some(
      (category) => !category.classList.contains('hidden')
    );
    setHasResults(hasVisibleCategories);
  }, [filter]);

  useEffect(() => {
    if (showModal && linkDialog.current && dialogRef.current) {
      dialogRef.current.focus();
      const linkElements = linkDialog.current.querySelectorAll(
        'a[href], button, input, [tabindex]:not([tabindex="-1"])'
      );
      if (linkElements.length > 0) {
        const firstElement = linkElements[0] as HTMLElement;
        const lastElement = linkElements[linkElements.length - 1] as HTMLElement;

        // If we are in the searchinput, enforce staying here until a tab event occurs
        if (searchInputRef.current) {
          searchInputRef.current?.focus();
        }
        const catchTabKey = (e: KeyboardEvent) => {
          if (e.key === 'Tab') {
            if (!dialogRef.current) return;
            if (!dialogRef.current.contains(document.activeElement)) {
              firstElement.focus();
              //   return;
            }
            if (e.shiftKey) {
              if (document.activeElement === firstElement) {
                e.preventDefault();
                lastElement.focus();
              }
            } else {
              if (document.activeElement === lastElement) {
                e.preventDefault();
                firstElement.focus();
              }
            }
          }
        };
        document.addEventListener('keydown', catchTabKey);
        return () => {
          document.removeEventListener('keydown', catchTabKey);
        };
      }
    }
    return undefined;
  }, [showModal, filter]);

  if (!showModal) return null;

  return (
    <dialog
      ref={dialogRef}
      tabIndex={-1}
      className="fixed w-full h-full inset-0 overflow-y-auto z-[9999] bg-scrim-clickable"
      open
    >
      <div
        className="fixed w-full h-full"
        onClick={() => {
          if (onCloseAll) {
            onCloseAll();
          } else {
            setShowModal(false);
          }
          setFilter('');
        }}
      ></div>
      <div className="absolute top-0 lg:top-[10%] left-0 right-0 mx-auto w-full md:max-w-[480px] lg:max-w-[540px] max-h-[83.3%] border bg-light-background-neutral-variant p-4 md:p-6 lg:p-8 overflow-auto bok-scrollbar bok-scrollbar--no-border">
        <div ref={linkDialog}>
          <div className="flex justify-between">
            <button
              className="text-light-fill-primary-variant p3 flex items-center gap-x-2"
              onClick={() => {
                // Atomic return to login (if provided) prevents blank frame flicker.
                if (onGoBack) {
                  onGoBack();
                } else {
                  setShowModal(false);
                }
                setFilter('');
              }}
            >
              <SVG
                className="[&_svg]:h-4 [&_svg]:w-4 flex [&_path]:stroke-light-fill-primary-variant"
                svg="icon-back"
              ></SVG>
              <span>{additionalLinks?.fields?.['Go Back Text']?.value}</span>
            </button>
            <button
              aria-label="Close Dialog"
              onClick={() => {
                if (onCloseAll) {
                  onCloseAll();
                } else {
                  setShowModal(false);
                  clearSearch();
                }
              }}
            >
              <SVG
                className="[&_svg]:w-6 [&_svg]:h-6 [&_path]:fill-light-fill-primary-variant"
                svg="icon-close"
              ></SVG>
            </button>
          </div>
          <RichTextA11yWrapper
            field={additionalLinks?.fields?.['Modal Header Text']}
            tag="h3"
            className="text-center mt-2"
          ></RichTextA11yWrapper>
          <RichTextA11yWrapper
            field={additionalLinks?.fields?.['Modal Subheader Text']}
            className="p2 text-center mt-2"
          ></RichTextA11yWrapper>
          {/* Search box */}
          <div className="text-center flex items-center my-6">
            <input
              type="search"
              name="Search"
              maxLength={39}
              placeholder={additionalLinks?.fields?.['Search Placeholder']?.value}
              aria-label="Filter links"
              className="bokf-search border rounded-l-full border-r-0 py-2 pl-4 w-full h-10 caret-light-fill-secondary placeholder-light-fill-primary-variant border-light-stroke-primary"
              onChange={(e) => filterLinks(e.target.value)}
              ref={searchInputRef}
            />
            <button
              id="clear-filter-input"
              aria-label="Clear Filter"
              className={`bok-clear-input border border-l-0 border-light-stroke-primary h-10 px-[14px] ${
                enableClearSearch ? '' : 'hidden'
              }`}
              onClick={clearSearch}
            >
              <SVG
                className="[&_svg]:h-3 [&_svg]:w-3 [&_path]:fill-light-fill-primary-variant [&_path]:hover:fill-light-fill-secondary"
                svg="icon-close"
              ></SVG>
            </button>
            <SVG
              svg="icon-search2"
              className="p-2 h-10 min-w-[46px] rounded-r-full flex items-center justify-center bg-light-background-gradient hover:bg-light-background-gradient-hover focus:bg-light-background-gradient-focus outline-none focus:outline-none [&_svg]:h-5 [&_svg]:w-5 [&_path]:stroke-light-fill-inverse"
            ></SVG>
          </div>
          {/* Error message  */}
          {filter !== '' && !hasResults && (
            <div className="flex items-center justify-center gap-x-2 text-color-default-1 font-bold p2-medium word-break-break-word">
              <SVG
                className="[&_path]:fill-light-fill-feedback-negative"
                svg="icon-filled-warning"
              ></SVG>
              {`${
                additionalLinks?.fields?.['No Results Message']?.value || 'No results found for '
              } "${filter}"`}
            </div>
          )}
          {/* Additional Links List */}
          <div className="flex flex-col gap-y-4">
            {additionalLinks?.fields?.children?.map((category: LoginLinkCollection) => {
              const filteredLinks = category?.fields?.children?.filter(
                (item: LoginLinkCollection) => {
                  if (
                    item?.fields?.Text?.value.toLowerCase().includes(filter.toLowerCase()) &&
                    item.fields?.Link?.value?.href
                  ) {
                    return true;
                  }
                  return false;
                }
              );
              // Use a unique key from category, not array index
              const categoryKey = category?.fields?.Text?.value || Math.random().toString();
              return (
                <div
                  key={categoryKey}
                  data-link-category={category?.fields?.Text?.value}
                  className={`${filteredLinks.length > 0 ? '' : 'hidden'}`}
                >
                  <RichTextA11yWrapper
                    tag="div"
                    field={category?.fields?.Text}
                    editable={false}
                    className="p3-medium w-full border-b border-light-stroke-secondary-brand"
                  />
                  <ul
                    className={`list-none mt-2 ${filter.length >= 2 ? 'columns-1' : 'columns-2'}`}
                  >
                    {filteredLinks.map((link: LoginLinkCollection, index: number) => {
                      const linkKey = link?.fields?.Text?.value || Math.random().toString();
                      return (
                        <li
                          key={linkKey}
                          className={index % 2 ? '' : 'bg-light-background-neutral-high'}
                        >
                          <a
                            href={link?.fields?.Link?.value?.href}
                            target={link?.fields?.Link?.value?.target}
                            className="px-2 lg:pl-2 lg:pr-3 py-3 md:py-4 lg:py-[.875rem] gap-x-2 c1 text-light-fill-secondary flex justify-between items-center hover:underline word-break-break-word"
                          >
                            {link?.fields?.Text?.value}
                            <SVG
                              className="[&_svg]:h-4 [&_svg]:w-4 [&_path]:stroke-light-stroke-primary-variant"
                              svg="icon-box-with-arrow"
                            ></SVG>
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </dialog>
  );
};

export default AdditionalLinksModal;
